import { request } from '@umijs/max';
import { logoutBoss as logoutApi } from '@/services/core/bossauth';
import { getCurrentUserPermissionSnapshot } from '@/services/core/bosspermission';
import { getBossUserInfo } from '@/services/core/bossuser';
import {
  type CurrentUser,
  type CurrentUserInput,
  clearAuthSession,
  getAuthSession,
  getAuthSessionGeneration,
  getAuthSessionIdentity,
  getStoredCurrentUser,
  isAccessTokenExpired,
  normalizeCurrentUser,
  setAuthSession,
  setStoredCurrentUser,
  updateAuthSessionTokens,
} from './authSession';
import {
  beginPermissionRecovery,
  failPermissionRecovery,
  getPermissionRecoveryState,
  resetPermissionSnapshotResolver,
  setPermissionSnapshot,
} from './mockPermissions';

export interface PasswordLoginParams {
  username?: string;
  password?: string;
  autoLogin?: boolean;
}

export interface PasswordLoginResult {
  status: 'ok' | 'error';
  type: 'account';
  currentAuthority?: string;
  errorMessage?: string;
  currentUser?: CurrentUser;
}

interface TokenEndpointPayload {
  accessToken?: string;
  access_token?: string;
  expiresIn?: number | string;
  expires_in?: number | string;
  accessTokenExpiresAt?: string | number;
  access_token_expires_at?: string | number;
  user?: CurrentUserInput;
  currentUser?: CurrentUserInput;
  current_user?: CurrentUserInput;
}

type TokenEndpointResponse = TokenEndpointPayload & {
  success?: boolean;
  data?: TokenEndpointPayload;
  error?: string;
  error_description?: string;
  errorMessage?: string;
  uniformErrorMsg?: string;
};

let pendingRefresh: { owner: string; promise: Promise<boolean> } | undefined;

let pendingPermissions: { owner: string; promise: Promise<void> } | undefined;
let pendingRecovery: { owner: string; promise: Promise<void> } | undefined;
const sessionOwner = () =>
  `${getAuthSessionGeneration()}:${getAuthSessionIdentity() ?? ''}:${getAuthSession()?.currentUser?.id ?? ''}`;

export class AuthRecoveryError extends Error {
  constructor() {
    super('登录状态暂时无法恢复，请检查网络后重试');
    this.name = 'AuthRecoveryError';
  }
}

const readAuthFailure = (
  error: unknown,
): { status: number | undefined; codes: string[] } => {
  if (!error || typeof error !== 'object') return { status: undefined, codes: [] as string[] };
  const value = error as {
    status?: number;
    error?: string;
    errorCode?: string | number;
    uniformErrorCode?: string | number;
    response?: { status?: number; data?: unknown };
    data?: unknown;
    info?: unknown;
  };
  const nested = value.response?.data ?? value.info ?? value.data;
  const inner =
    nested && nested !== error ? readAuthFailure(nested) : undefined;
  return {
    status: value.response?.status ?? value.status ?? inner?.status,
    codes: [value.error, value.errorCode, value.uniformErrorCode]
      .filter((code) => code !== undefined)
      .map(String)
      .concat(inner?.codes ?? []),
  };
};
const invalidRefreshCredentials = (error: unknown) => {
  const { status, codes } = readAuthFailure(error);
  return (
    status === 401 ||
    codes.some((code) =>
      [
        'invalid_grant',
        'invalid_token',
        'AUTH_REFRESH_EXPIRED',
        'AUTH_USER_DISABLED',
        '401',
        '100401',
        'UNAUTHORIZED',
      ].includes(code))
  );
};
const permissionDenied = (error: unknown) => {
  const { status, codes } = readAuthFailure(error);
  return (
    status === 403 ||
    codes.some((code) =>
      ['403', '100403', 'FORBIDDEN', 'AUTH_NO_PERMISSION'].includes(code))
  );
};

const DEFAULT_AUTH_CLIENT_ID = 'conference-wan';
const DEFAULT_AUTH_CLIENT_SECRET = 'dev-secret';

const getRuntimeEnv = (key: string) => {
  if (typeof process === 'undefined') {
    return undefined;
  }
  return (process.env as Record<string, string | undefined>)[key];
};

const createFormBody = (body: Record<string, string | undefined>) => {
  const params = new URLSearchParams();
  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, value);
    }
  });
  return params.toString();
};

const getAuthClientFields = () => {
  const clientId =
    getRuntimeEnv('REACT_APP_AUTH_CLIENT_ID') ||
    getRuntimeEnv('AUTH_CLIENT_ID') ||
    DEFAULT_AUTH_CLIENT_ID;
  const clientSecret =
    getRuntimeEnv('REACT_APP_AUTH_CLIENT_SECRET') ||
    getRuntimeEnv('AUTH_CLIENT_SECRET') ||
    DEFAULT_AUTH_CLIENT_SECRET;

  return {
    client_id: clientId,
    client_secret: clientSecret,
  };
};

const getTokenErrorMessage = (response: TokenEndpointResponse) =>
  response.error_description ||
  response.errorMessage ||
  response.uniformErrorMsg ||
  response.error ||
  '登录响应缺少令牌';

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error) {
    const typedError = error as {
      data?: TokenEndpointResponse;
      info?: TokenEndpointResponse;
      errorMessage?: string;
      message?: string;
      response?: { data?: TokenEndpointResponse };
    };
    const responseData =
      typedError.data || typedError.info || typedError.response?.data;
    if (responseData) {
      return getTokenErrorMessage(responseData);
    }
    return typedError.errorMessage || typedError.message || '未知错误';
  }
  return String(error || '未知错误');
};

const extractTokenPayload = (response: TokenEndpointResponse) => {
  const data = response.data || response;
  const accessToken = data.accessToken || data.access_token;
  if (!accessToken) {
    throw new Error(getTokenErrorMessage(response));
  }
  return {
    accessToken,
    expiresIn: data.expiresIn ?? data.expires_in,
    accessTokenExpiresAt:
      data.accessTokenExpiresAt ?? data.access_token_expires_at,
    currentUser: normalizeCurrentUser(
      data.currentUser || data.current_user || data.user,
    ),
  };
};

const mapPermissionGroups = (
  groups?: CoreAPI.PermissionGroupSummaryVO[],
): CurrentUser['permissionGroups'] =>
  groups?.map((group) => ({
    id:
      group.id === undefined || group.id === null
        ? undefined
        : String(group.id),
    code: group.permissionGroupCode || '',
    name: group.permissionGroupName || '',
    preset: group.isPreset === 1,
  }));

const mapUserVO = (user?: CoreAPI.UserVO): CurrentUser | undefined => {
  if (!user) {
    return undefined;
  }
  const userWithFirstLogin = user as CoreAPI.UserVO & {
    isFirstLogin?: boolean;
  };
  const cachedUser = getStoredCurrentUser();
  const id =
    user.id === undefined || user.id === null ? undefined : String(user.id);
  return normalizeCurrentUser({
    id,
    userid: id || user.username,
    username: user.username,
    name: user.nickName || user.username,
    nickName: user.nickName,
    phone: user.phone,
    email: user.email,
    status: user.status,
    avatar: user.avatar || cachedUser?.avatar,
    wechat: user.wechat || cachedUser?.wechat,
    isFirstLogin: userWithFirstLogin.isFirstLogin ?? cachedUser?.isFirstLogin,
    permissionGroups:
      mapPermissionGroups(user.permissionGroups) ||
      cachedUser?.permissionGroups,
  });
};

const mapFieldPermissionBehavior = (
  rule: CoreAPI.FieldPermissionRuleVO,
): 'show' | 'disable' | 'mask' => {
  if (rule.viewLevel === 'MASKED') {
    return 'mask';
  }
  if (rule.editLevel === 'READONLY') {
    return 'disable';
  }
  return 'show';
};

export const syncCurrentUserPermissionSnapshot = async () => {
  const owner = sessionOwner();
  const userId = getAuthSession()?.currentUser?.id;
  if (!getAuthSessionIdentity() || !userId) {
    resetPermissionSnapshotResolver();
    return;
  }
  if (pendingPermissions?.owner === owner) return pendingPermissions.promise;
  const isCurrent = () => owner === sessionOwner();
  beginPermissionRecovery();
  const promise = (async () => {
    try {
      const response = await getCurrentUserPermissionSnapshot(
        {},
        { skipErrorHandler: true, handleForbiddenLocally: true },
      );
      if (!isCurrent()) return;
      if (response.success === false || !response.data) {
        throw response;
      }
      const snapshot = response.data;
      if (String(snapshot.userId ?? '') !== userId) throw new Error('权限快照身份不匹配');
      setPermissionSnapshot({
        source: 'api',
        userId: snapshot.userId,
        permissionCodes: snapshot.permissionCodes || [],
        permissionGroups: (snapshot.permissionGroups || []).map(
          (group: CoreAPI.UserPermissionGroupSimpleVO) => ({
            id:
              group.id === undefined || group.id === null
                ? undefined
                : String(group.id),
            code: group.permissionGroupCode || '',
            name: group.permissionGroupName || '',
            preset: group.isPreset === 1,
          }),
        ),
        fieldPermissions: Object.fromEntries(
          (snapshot.fieldRules || [])
            .filter((rule: CoreAPI.FieldPermissionRuleVO) =>
              Boolean(rule.fieldCode))
            .map((rule: CoreAPI.FieldPermissionRuleVO) => [
              `${rule.resourceCode || ''}.${rule.fieldCode}`,
              {
                fieldKey: `${rule.resourceCode || ''}.${rule.fieldCode}`,
                resourceCode: rule.resourceCode,
                fieldName: rule.fieldName,
                behavior: mapFieldPermissionBehavior(rule),
                viewLevel: rule.viewLevel as 'FULL' | 'MASKED' | undefined,
                editLevel: rule.editLevel as
                  | 'EDITABLE'
                  | 'READONLY'
                  | undefined,
                maskRuleCode: rule.maskRuleCode,
                maskText: rule.maskRuleCode ? '***' : undefined,
                reason: '后端权限快照返回的字段权限规则',
              },
            ]),
        ),
        updatedAt: snapshot.refreshedAt,
      });
    } catch (error) {
      if (isCurrent()) failPermissionRecovery(permissionDenied(error));
    } finally {
      if (pendingPermissions?.owner === owner) pendingPermissions = undefined;
    }
  })();
  pendingPermissions = { owner, promise };
  return promise;
};

export const loginWithPassword = async (
  params: PasswordLoginParams,
): Promise<PasswordLoginResult> => {
  const username = params.username?.trim();
  const { password } = params;
  if (!username || !password) {
    return {
      status: 'error',
      type: 'account',
      currentAuthority: 'guest',
      errorMessage: '请输入用户名和密码',
    };
  }

  try {
    const response = await request<TokenEndpointResponse>(
      '/bossApi/conference/core/auth/v1/oauth2/token',
      {
        method: 'POST',
        withCredentials: true,
        skipErrorHandler: true,
        authSkipRefresh: true,
        authSkipAuthorization: true,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: createFormBody({
          grant_type: 'password',
          username,
          password,
          ...getAuthClientFields(),
        }),
      },
    );
    const session = setAuthSession(extractTokenPayload(response));
    return {
      status: 'ok',
      type: 'account',
      currentAuthority: session.currentUser?.access,
      currentUser: session.currentUser,
    };
  } catch (error) {
    clearAuthSession();
    return {
      status: 'error',
      type: 'account',
      currentAuthority: 'guest',
      errorMessage: getErrorMessage(error),
    };
  }
};

export const refreshAuthSession = async (): Promise<boolean> => {
  const owner = sessionOwner();
  if (pendingRefresh?.owner === owner) return pendingRefresh.promise;
  if (!getAuthSession()) return false;
  const current = () => owner === sessionOwner();
  const promise = (async () => {
    try {
      const response = await request<TokenEndpointResponse>(
        '/bossApi/conference/core/auth/v1/oauth2/token',
        {
          method: 'POST',
          withCredentials: true,
          skipErrorHandler: true,
          authSkipRefresh: true,
          authSkipAuthorization: true,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          data: createFormBody({
            grant_type: 'refresh_token',
            ...getAuthClientFields(),
          }),
        },
      );
      if (!current()) return false;
      if (invalidRefreshCredentials(response)) {
        clearAuthSession();
        return false;
      }
      const tokenPayload = extractTokenPayload(response);
      // Cookie-based renewal may omit the optional user summary's ID.
      // Only an identified summary can replace or conflict with the cached user.
      const refreshedUser = tokenPayload.currentUser?.id
        ? tokenPayload.currentUser
        : undefined;
      if (
        refreshedUser &&
        refreshedUser.id !== getAuthSession()?.currentUser?.id
      ) {
        throw new AuthRecoveryError();
      }
      updateAuthSessionTokens(tokenPayload);
      if (!current()) return false;
      if (refreshedUser) {
        setStoredCurrentUser(refreshedUser);
      }
      return true;
    } catch (error) {
      if (!current()) return false;
      if (invalidRefreshCredentials(error)) {
        clearAuthSession();
        return false;
      }
      throw new AuthRecoveryError();
    } finally {
      if (pendingRefresh?.owner === owner) pendingRefresh = undefined;
    }
  })();
  pendingRefresh = { owner, promise };
  return promise;
};

/** Foreground/request recovery has no keepalive timer and never replays a business mutation. */
export const recoverAuthSession = async () => {
  const owner = sessionOwner();
  if (!getAuthSession()) return;
  if (pendingRecovery?.owner === owner) return pendingRecovery.promise;
  const promise = Promise.resolve().then(async () => {
    try {
      if (isAccessTokenExpired() && !(await refreshAuthSession())) return;
      if (owner !== sessionOwner()) return;
      if (!getAuthSession()?.currentUser?.id) {
        const user = await getSessionCurrentUser();
        if (!user && owner === sessionOwner()) failPermissionRecovery();
        return;
      }
      const { status } = getPermissionRecoveryState();
      if (status !== 'ready' && status !== 'denied') await syncCurrentUserPermissionSnapshot();
    } catch (error) {
      if (
        owner === sessionOwner() &&
        getPermissionRecoveryState().status !== 'ready'
      ) failPermissionRecovery();
      throw error;
    } finally {
      if (pendingRecovery?.owner === owner) pendingRecovery = undefined;
    }
  });
  pendingRecovery = { owner, promise };
  return promise;
};

export const getSessionCurrentUser = async () => {
  const owner = sessionOwner();
  if (!getAuthSession()) return undefined;
  const cachedUser = getStoredCurrentUser();
  try {
    const response = await getBossUserInfo(
      {},
      {
        skipErrorHandler: true,
        handleForbiddenLocally: true,
      },
    );
    if (owner !== sessionOwner()) return undefined;
    if (response.success === false) throw new Error(response.errorMessage || '获取当前用户失败');
    const currentUser = mapUserVO(response.data) || cachedUser;
    if (cachedUser?.id && currentUser?.id !== cachedUser.id) throw new Error('用户响应身份不匹配');
    setStoredCurrentUser(currentUser);
    const updatedOwner = sessionOwner();
    await syncCurrentUserPermissionSnapshot();
    return updatedOwner === sessionOwner() ? currentUser : undefined;
  } catch {
    return owner === sessionOwner() ? cachedUser : undefined;
  }
};

export const logoutSession = async () => {
  const owner = sessionOwner();
  try {
    if (getAuthSession()) {
      await logoutApi({
        skipErrorHandler: true,
        withCredentials: true,
        authSkipRetry: true,
      });
    }
  } finally {
    if (owner === sessionOwner()) {
      clearAuthSession();
      resetPermissionSnapshotResolver();
    }
  }
};
